import React, { useState } from 'react';
import { UserX, AlertTriangle, Ban, Shield, Lock, MoreHorizontal, Flag, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogBody } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';

interface ProfileData {
  firstName?: string;
  lastName?: string;
  fullName: string;
  jobTitle: string;
  photo?: string | null;
}

interface ReportModalProps {
  profileData: ProfileData;
  onClose: () => void;
  onSubmit?: (reason: string, details: string) => void;
}

interface ReportReason {
  value: string;
  label: string;
  icon: React.ReactNode;
}

const ReportModal: React.FC<ReportModalProps> = ({ profileData, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const reportReasons: ReportReason[] = [
    { value: 'impersonation', label: t('Impersonation'), icon: <UserX className="w-5 h-5" /> },
    { value: 'inappropriate_content', label: t('Inappropriate Content'), icon: <AlertTriangle className="w-5 h-5" /> },
    { value: 'spam', label: t('Spam or Misleading'), icon: <Ban className="w-5 h-5" /> },
    { value: 'harassment', label: t('Harassment'), icon: <Shield className="w-5 h-5" /> },
    { value: 'privacy_violation', label: t('Privacy Violation'), icon: <Lock className="w-5 h-5" /> },
    { value: 'other', label: t('Other'), icon: <MoreHorizontal className="w-5 h-5" /> }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e?.preventDefault();
    if (selectedReason) {
      onSubmit?.(selectedReason, details);
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };

  if (submitted) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent size="default" hideCloseButton>
          <DialogBody className="p-8 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-success" />
            </div>
            <DialogTitle className="text-2xl font-bold text-foreground mb-2">{t('Report Submitted')}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{t('Thank you for helping us maintain a safe community. We\'ll review your report shortly.')}</DialogDescription>
          </DialogBody>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent size="default" className="max-w-lg">
        <DialogHeader className="p-6 border-b-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center">
              <Flag className="w-5 h-5 text-destructive" />
            </div>
            <DialogTitle className="text-2xl font-bold text-foreground">{t('Report Profile')}</DialogTitle>
          </div>
        </DialogHeader>

        <DialogBody className="px-6 pb-0 pt-0">
          {/* Profile Info */}
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg mb-6">
            {profileData?.photo ? (
              <img
                src={profileData?.photo}
                alt={profileData?.fullName}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center">
                <span className="text-lg font-bold text-white">
                  {profileData?.firstName?.[0]}{profileData?.lastName?.[0]}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate">{profileData?.fullName}</p>
              <p className="text-sm text-muted-foreground truncate">{profileData?.jobTitle}</p>
            </div>
          </div>

          {/* Form */}
          <form id="report-form" onSubmit={handleSubmit}>
            {/* Reason Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-foreground mb-3">
                {t('Why are you reporting this profile?')} *
              </label>
              <div className="space-y-2">
                {reportReasons?.map((reason) => (
                  <button
                    key={reason?.value}
                    type="button"
                    onClick={() => setSelectedReason(reason?.value)}
                    className={`w-full flex items-center gap-3 p-3 border-2 rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                      selectedReason === reason?.value
                        ? 'border-destructive bg-destructive/5' : 'border-border hover:border-muted-foreground bg-card'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedReason === reason?.value ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
                    }`}>
                      {reason?.icon}
                    </div>
                    <span className={`font-medium ${
                      selectedReason === reason?.value ? 'text-destructive' : 'text-foreground'
                    }`}>
                      {reason?.label}
                    </span>
                    {selectedReason === reason?.value && (
                      <Check className="w-5 h-5 text-destructive ms-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Details */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-foreground mb-2">
                {t('Additional Details (Optional)')}
              </label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e?.target?.value)}
                placeholder={t('Please provide any additional information that might help us review this report...')}
                rows={4}
                className="w-full px-4 py-3 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none text-foreground"
              />
            </div>

            {/* Info Box - keeping blue intentionally */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <div className="text-primary flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-sm text-foreground">
                  <p className="font-medium mb-1">{t('Your report is confidential')}</p>
                  <p className="text-muted-foreground">
                    {t('We take all reports seriously and will investigate promptly. The profile owner will not be notified of who submitted the report.')}
                  </p>
                </div>
              </div>
            </div>
          </form>
        </DialogBody>

        {/* Actions */}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="destructive"
            type="submit"
            form="report-form"
            disabled={!selectedReason}
          >
            {t('Submit Report')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportModal;
